import { inject } from 'mobx-react';
import * as React from 'react';

import { Layer, ViewMedium, Viewport, ViewSmall } from 'bernie-view';
import { QueryRoute, RouteStore } from 'bernie-core';

import {
  PropertySpaceOverview,
  PropertySummaryAmenities,
  PropertySummaryCleaningAndSafety,
  PropertySummaryHeader,
  PropertySummaryMapTrigger,
  PropertySummaryNearByPois,
  PropertySummaryReviews,
} from '@shared-ui/lodging-property-details';

import { CurrentHotelStore } from 'src/common/stores/current-hotel-store';

import { UitkCardAloha, UitkCardAlohaContentSection } from 'uitk-react-cards';
import { UitkFigureAspectRatioType } from 'uitk-react-images';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkSpacing } from 'uitk-react-spacing';

import { SharedUiMap } from 'components/infosite/shared-ui/details/map-wrapper';
import { SharedUiPropertyReviewsDialog } from 'components/infosite/shared-ui/reviews/shared-ui-property-reviews-dialog';
import { SharedUiWrapperProps, withSharedUiPropertyDetails } from 'components/infosite/shared-ui/details/shared-ui-property-details';

interface SummaryWrapperProps extends SharedUiWrapperProps {
  routeStore?: RouteStore;
}

const SummaryWrapper: React.FC<SummaryWrapperProps> = inject('routeStore')(
  React.memo(({ inputs, routeStore }) => {
    /* istanbul ignore next */
    const onClickMap = React.useCallback(() => routeStore?.updateSearch({ pwaDialog: 'map' }), [routeStore]);

    /* istanbul ignore next */
    const onClickReviews = React.useCallback(() => routeStore?.updateSearch({ pwaDialog: 'reviews' }), [routeStore]);

    return (
      <UitkSpacing margin={{ blockend: 'six', blockstart: 'half' }}>
        <UitkCardAloha className="uitk-flat-border-top">
          <UitkCardAlohaContentSection>
            <UitkSpacing padding={{ large: { inline: 'three', block: 'three' } }}>
              <UitkLayoutFlex direction="column">
                <>
                  <UitkLayoutFlexItem>
                    <UitkSpacing
                      padding={{ blockend: 'three' }}
                      margin={{ small: { blockstart: 'three' }, medium: { blockstart: 'unset' } }}
                    >
                      <div data-stid="content-hotel-title">
                        <PropertySummaryHeader inputs={inputs} />
                      </div>
                    </UitkSpacing>
                  </UitkLayoutFlexItem>
                  <UitkLayoutFlexItem>
                    <UitkSpacing padding={{ blockend: 'three' }} margin={{ blockstart: 'three' }}>
                      <PropertySummaryReviews inputs={inputs} onClickReviews={onClickReviews} />
                    </UitkSpacing>
                  </UitkLayoutFlexItem>
                  <UitkLayoutFlexItem>
                    <UitkSpacing padding={{ blockend: 'three' }} margin={{ blockstart: 'three' }}>
                      <PropertySpaceOverview inputs={inputs} />
                    </UitkSpacing>
                  </UitkLayoutFlexItem>
                  <UitkLayoutFlexItem>
                    <UitkSpacing padding={{ small: { blockend: 'three' } }} margin={{ blockstart: 'three' }}>
                      <PropertySummaryAmenities inputs={inputs} />
                    </UitkSpacing>
                  </UitkLayoutFlexItem>
                  <UitkLayoutFlexItem>
                    <UitkSpacing margin={{ blockstart: 'four' }}>
                      <PropertySummaryCleaningAndSafety inputs={inputs} />
                    </UitkSpacing>
                  </UitkLayoutFlexItem>
                  <UitkLayoutFlexItem>
                    <div>
                      <Viewport>
                        <ViewSmall>
                          <>
                            <div data-stid="map-image-link">
                              <UitkSpacing margin={{ blockstart: 'three' }}>
                                <PropertySummaryMapTrigger
                                  inputs={inputs}
                                  onClickMap={onClickMap}
                                  aspectRatio={UitkFigureAspectRatioType.R21_9}
                                />
                              </UitkSpacing>
                            </div>
                            <div data-stid="location-summary">
                              <UitkSpacing margin={{ blockstart: 'three' }}>
                                <PropertySummaryNearByPois inputs={inputs} />
                              </UitkSpacing>
                            </div>
                          </>
                        </ViewSmall>
                        <ViewMedium>
                          <UitkLayoutGrid space="three" columns={2}>
                            <UitkLayoutGridItem>
                              <div data-stid="map-image-link">
                                <UitkSpacing margin={{ blockstart: 'three' }}>
                                  <PropertySummaryMapTrigger
                                    inputs={inputs}
                                    onClickMap={onClickMap}
                                    aspectRatio={UitkFigureAspectRatioType.R21_9}
                                  />
                                </UitkSpacing>
                              </div>
                            </UitkLayoutGridItem>
                            <UitkLayoutGridItem>
                              <div data-stid="location-summary">
                                <UitkSpacing margin={{ blockstart: 'three' }}>
                                  <PropertySummaryNearByPois inputs={inputs} />
                                </UitkSpacing>
                              </div>
                            </UitkLayoutGridItem>
                          </UitkLayoutGrid>
                        </ViewMedium>
                      </Viewport>
                    </div>
                  </UitkLayoutFlexItem>
                </>
              </UitkLayoutFlex>
            </UitkSpacing>
          </UitkCardAlohaContentSection>
          <MapDialogWrapper />
          <ReviewsDialogWrapper />
        </UitkCardAloha>
      </UitkSpacing>
    );
  })
);

interface SummaryDialogProps {
  currentHotel?: CurrentHotelStore;
  routeStore?: RouteStore;
}

const ReviewsDialogWrapper: React.FC<Pick<SummaryDialogProps, 'routeStore'>> = inject('routeStore')(({ routeStore }) => {
  /* istanbul ignore next */
  const onCloseDialog = React.useCallback(() => routeStore?.goBack(), [routeStore]);

  return (
    <QueryRoute query={{ pwaDialog: 'reviews' }}>
      <SharedUiPropertyReviewsDialog onDialogClose={onCloseDialog} />
    </QueryRoute>
  );
});
ReviewsDialogWrapper.displayName = 'ReviewsDialogWrapper';

const MapDialogWrapper: React.FC<SummaryDialogProps> = inject('routeStore')(({ routeStore }) => {
  /* istanbul ignore next */
  const onCloseDialog = React.useCallback(() => routeStore?.goBack(), [routeStore]);

  return (
    <QueryRoute query={{ pwaDialog: 'map' }}>
      <Layer id="infosite-map" onDismiss={onCloseDialog}>
        <SharedUiMap onClose={onCloseDialog} />
      </Layer>
    </QueryRoute>
  );
});
MapDialogWrapper.displayName = 'MapDialogWrapper';

export const SingleOfferSummaryWrapper = withSharedUiPropertyDetails<SharedUiWrapperProps>(SummaryWrapper);
SingleOfferSummaryWrapper.displayName = 'SingleOfferSummaryWrapper';
